import React, { useEffect, useState } from 'react';
import './SpotifyReauthBanner.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const POLL_MS = 5 * 60 * 1000; // every 5 minutes is plenty for a 6-month clock

// Renders a top-of-page banner when the Spotify refresh token is either
// already dead (needsReauth) or inside the 14-day pre-expiry window.
// Driven by /api/services/spotify/diagnostic which exposes the lifetime
// fields added for the 2026-06-18 refresh-token expiration announcement.
function SpotifyReauthBanner() {
  const [status, setStatus] = useState(null);
  const [dismissedForDays, setDismissedForDays] = useState(() => {
    const raw = sessionStorage.getItem('spotifyReauthBannerDismissed');
    return raw ? parseInt(raw, 10) : null;
  });

  useEffect(() => {
    let cancelled = false;

    async function fetchStatus() {
      try {
        const res = await fetch(`${API_BASE}/services/spotify/diagnostic`, {
          credentials: 'include'
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setStatus(data?.reauth || null);
      } catch (_err) {
        // Banner is best-effort; never blow up the page if diagnostic fails.
      }
    }

    fetchStatus();
    const timer = setInterval(fetchStatus, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  if (!status) return null;
  const { needsReauth, daysUntilReauth, authUrl } = status;
  const inWarningWindow =
    typeof daysUntilReauth === 'number' && daysUntilReauth <= 14;

  if (!needsReauth && !inWarningWindow) return null;

  // Allow dismissing a "warning" banner for the current day-count, but
  // never let users hide the hard-expired banner — the app is broken.
  if (!needsReauth && dismissedForDays === daysUntilReauth) return null;

  const reauthHref = authUrl || '/auth/spotify';
  const isExpired = needsReauth;
  const text = isExpired
    ? 'Spotify access expired — playlist sync and generator are paused.'
    : `Spotify access expires in ${daysUntilReauth} day${
        daysUntilReauth === 1 ? '' : 's'
      }. Re-authorize to keep sync running.`;

  return (
    <div
      className={`spotify-reauth-banner ${isExpired ? 'expired' : 'warning'}`}
      role={isExpired ? 'alert' : 'status'}
    >
      <span className="spotify-reauth-banner__text">{text}</span>
      <a
        className="spotify-reauth-banner__cta"
        href={reauthHref}
        target="_blank"
        rel="noopener noreferrer"
      >
        Re-authorize Spotify
      </a>
      {!isExpired && (
        <button
          type="button"
          className="spotify-reauth-banner__dismiss"
          onClick={() => {
            setDismissedForDays(daysUntilReauth);
            sessionStorage.setItem(
              'spotifyReauthBannerDismissed',
              String(daysUntilReauth)
            );
          }}
          aria-label="Dismiss until token state changes"
        >
          ×
        </button>
      )}
    </div>
  );
}

export default SpotifyReauthBanner;
